import { Component, Element, Event, EventEmitter, h, Host, Listen, State, Watch } from '@stencil/core';
import { TiResponsive, TiResponsiveHandler, TiResponsiveViewport } from '@ticom/web-component-utils';
import { StickyHeaderChangeEventDetail } from './sticky-header.interface';

@Component({
    tag: 'ti-sticky-header',
    styleUrl: 'sticky-header.scss',
    shadow: true
})
export class StickyHeader implements TiResponsive {
    /**
     * Element holding the persistent bottom section (usually the navbar).
     */
    private _bottomEl: HTMLDivElement;

    private _componentLoaded: boolean = false;

    /**
     * Height value of the persistent bottom section. Used to fix the
     * height of the wrapper that becomes empty when the header becomes sticky.
     */
    private _fixedHeight: number;

    /**
     * Whether or not the section for top sticky content is used.
     */
    private _hasTopSlotContent: boolean;

    /**
     * An element on the page whose top position is used to trigger the sticky header appearance.
     */
    private _triggerElement: HTMLElement;

    /**
     * Element fixed on the page.
     */
    private _wrapperEl: HTMLDivElement;


    @Element() hostElement!: HTMLTiStickyHeaderElement;

    /**
     * State of whether the header's position is fixed to the top of the view or not
     */
    @State() sticky: boolean = false;
    @Watch('sticky')
    handleStickyChange(stickyValue) {
        // dispatch an event when this changes so that content
        // or page items can know and update accordingly
        this.tiStickyHeaderChange.emit({ sticky: stickyValue });
    }

    @TiResponsiveHandler()
    @State() viewportSize: TiResponsiveViewport;
    @Watch('viewportSize')
    handleViewportSizeChange(newSize: TiResponsiveViewport, oldSize: TiResponsiveViewport) {
        // reset the content heights when changing to or from desktop
        if (this._componentLoaded && (newSize === TiResponsiveViewport.desktop || oldSize === TiResponsiveViewport.desktop)) {
            // reset height and sticky related properties
            this._fixedHeight = undefined;
            this._componentLoaded = false;
            this.sticky = false;
            window.setTimeout(() => {
                this._setFixedHeight();
                this._componentLoaded = true;
                this._setSticky();
            }, 500); // allow time for page to settle
        }
    }

    /**
     * Event fired when the header's sticky value changes.
     *
     * @type {EventEmitter<StickyHeaderChangeEventDetail>}
     * @memberof StickyHeader
     */
    @Event() tiStickyHeaderChange: EventEmitter<StickyHeaderChangeEventDetail>;

    componentWillLoad() {
        this._hasTopSlotContent = !!this.hostElement.querySelector('[slot="top"]');
    }

    componentDidLoad() {
        this._setFixedHeight();
        this._componentLoaded = true;
        if (document.readyState === 'complete') {
            window.setTimeout(() => this._setSticky(), 50); // IE11 needs some delay
        }
    }

    disconnectedCallback() {
        // required by TiResponsive
    }

    /**
     * Once the page has finished loading, set the sticky state.
     */
    @Listen('load', { target: 'window' })
    onLoad() {
        this._triggerElement = document.getElementById('ti-sticky-header-trigger');
        if (this._componentLoaded) {
            window.setTimeout(() => this._setSticky(), 50); // IE11 needs some delay
        }
    }

    /**
     * Update the sticky state when scrolling.
     */
    @Listen('scroll', { target: 'window' })
    onScroll() {
        // set the fixed height if not set
        if (!this._fixedHeight) {
            this._setFixedHeight();
        }

        window.setTimeout(() => this._setSticky());
    }

    /**
     * Calculate and set the height for the fixed bottom section.
     */
    private _setFixedHeight() {
        // only do this if not sticky
        if (!this.sticky && this._bottomEl && this._wrapperEl) {
            this._fixedHeight = this._bottomEl.offsetHeight;
            this._wrapperEl.style.height = `${this._fixedHeight}px`;
        }
    }

    /**
     * Determine if the component should be sticky and set the state
     * to trigger a render.
     */
    private _setSticky() {
        if (this._componentLoaded && document.readyState === 'complete') {
            const top = (this._triggerElement || this.hostElement).getBoundingClientRect().top;
            this.sticky = top <= 0;
        }
    }

    render() {
        return (
            <Host class={this.viewportSize === TiResponsiveViewport.desktop && 'ti-sticky-header-desktop'} data-lid="sticky-header">
                <div ref={el => this._wrapperEl = el} class="ti-sticky-header-wrapper" style={{'height': this._fixedHeight ? `${this._fixedHeight}px` : 'auto'}}>
                    <div class={{
                        'ti-sticky-header-content': true,
                        'ti-sticky-header-sticky': this.sticky
                    }}>
                        <div class="ti-sticky-header-top" style={{
                            'display': this._hasTopSlotContent && this.sticky ? 'block': 'none'
                        }}>
                            <slot name="top" />
                        </div>
                        <div ref={el => this._bottomEl = el}>
                            <slot name="bottom" />
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
